import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { MoneyInput } from "../../../components/form-components/MoneyInput";
import { generateTierName } from "../policyBuilderState";

/**
 * TierEditorPanel — Self-contained workspace panel for tier details (name, amount toggle, min/max).
 * Shows placeholder when no tier selected. Prevents layout shift; stays in fixed column.
 */
const TierEditorPanel = ({
  tier,
  onTierChange,
  onAddTier,
  objectType,
  currency = "EUR",
  disabled,
  errors,
  autoFocusName,
  embedded,
}) => {
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (autoFocusName && tier && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  // Focus once per tier, not per tier object identity — tier is only read for existence.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocusName, tier?.id]);

  if (!tier && !embedded) {
    return (
      <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
        <div className="border-b border-border pb-3 mb-4">
          <h3 className="text-sm font-semibold text-text">Tier details</h3>
        </div>
        <div className="flex flex-col items-center justify-center flex-1 py-8 text-center">
          <p className="text-sm font-medium text-text-2 mb-1">No tier selected</p>
          <p className="text-sm text-text-2 mb-4">Select a tier on the left to edit, or add a new tier.</p>
          {!disabled && (
            <button
              type="button"
              onClick={onAddTier}
              className="text-primary hover:text-primary-hover text-sm font-medium cursor-pointer"
            >
              + Add tier
            </button>
          )}
        </div>
      </div>
    );
  }

  const tierErrors = (errors || []).filter((e) => e.tierId === tier.id);
  const formContent = (
    <>
      {!embedded && tierErrors.length > 0 && (
        <div className="px-3 py-2 bg-danger-soft border border-danger rounded-ctrl text-xs text-danger space-y-1 mb-4">
          {tierErrors.map((e, i) => (
            <div key={`${e.tierId || "tier"}-${e.message}-${i}`}>{e.message}</div>
          ))}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label htmlFor="tier-name-input" className="block text-xs font-medium text-text-2 mb-1">
            Tier name
          </label>
          <input
            id="tier-name-input"
            ref={nameInputRef}
            type="text"
            className="w-full p-2 border border-border rounded-ctrl text-sm"
            placeholder={generateTierName(tier)}
            value={tier.name}
            onChange={(e) => onTierChange({ ...tier, name: e.target.value })}
            disabled={disabled}
          />
        </div>
        <div>
          <label htmlFor="tier-any-amount" className="flex items-center gap-2 text-sm text-text-2">
            <input
              id="tier-any-amount"
              type="checkbox"
              checked={tier.any_amount}
              onChange={(e) => {
                const updated = { ...tier, any_amount: e.target.checked };
                if (e.target.checked) {
                  updated.min_amount = null;
                  updated.max_amount = null;
                }
                onTierChange(updated);
              }}
              disabled={disabled}
            />
            Any amount
          </label>
        </div>
        {!tier.any_amount && (
          <div className="flex gap-3 min-w-0">
            <div className="flex-1 min-w-0">
              <label htmlFor="tier-min-amount" className="block text-xs font-medium text-text-2 mb-1">
                Min amount
              </label>
              <MoneyInput
                value={tier.min_amount}
                onChange={(v) => onTierChange({ ...tier, min_amount: v })}
                currency={currency}
                placeholder="No minimum"
                allowNegative={false}
                maxWidth="100%"
              />
            </div>
            <div className="flex-1 min-w-0">
              <label htmlFor="tier-max-amount" className="block text-xs font-medium text-text-2 mb-1">
                Max amount
              </label>
              <MoneyInput
                value={tier.max_amount}
                onChange={(v) => onTierChange({ ...tier, max_amount: v })}
                currency={currency}
                placeholder="No maximum"
                allowNegative={false}
                maxWidth="100%"
              />
            </div>
          </div>
        )}
        {objectType === "budget_period_version" && (
          <div className="space-y-2 pt-2 border-t border-border">
            <label htmlFor="tier-near-term" className="flex items-center gap-2 text-sm text-text-2">
              <input
                id="tier-near-term"
                type="checkbox"
                checked={tier.context.near_term_only}
                onChange={(e) =>
                  onTierChange({
                    ...tier,
                    context: { ...tier.context, near_term_only: e.target.checked },
                  })
                }
                disabled={disabled}
              />
              Near-term only
            </label>
            {tier.context.near_term_only && (
              <div>
                <label htmlFor="tier-within-days" className="block text-xs font-medium text-text-2 mb-1">
                  Within days
                </label>
                <input
                  id="tier-within-days"
                  type="number"
                  className="w-full p-2 border border-border rounded-ctrl text-sm"
                  value={tier.context.within_days}
                  onChange={(e) =>
                    onTierChange({
                      ...tier,
                      context: {
                        ...tier.context,
                        within_days: Number.parseInt(e.target.value, 10) || 0,
                      },
                    })
                  }
                  disabled={disabled}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  if (embedded) {
    return formContent;
  }

  return (
    <div className="bg-card border border-card-bd rounded-card p-4 min-w-0">
      <div className="border-b border-border pb-3 mb-4">
        <h3 className="text-sm font-semibold text-text">Tier details</h3>
      </div>
      {formContent}
    </div>
  );
};

TierEditorPanel.propTypes = {
  tier: PropTypes.object,
  onTierChange: PropTypes.func.isRequired,
  onAddTier: PropTypes.func.isRequired,
  objectType: PropTypes.string,
  currency: PropTypes.string,
  disabled: PropTypes.bool,
  errors: PropTypes.array,
  autoFocusName: PropTypes.bool,
  embedded: PropTypes.bool,
};

export default TierEditorPanel;
